import { Link, useLocation } from 'react-router'
import type { JSX } from 'react'
import { useActiveTenant, useTenants } from './context'

/**
 * What an organisation whose free trial ran out sees instead of the product.
 *
 * AUTH-19, and the owner's decision of 2026-09-09: *"after that payment needs to
 * be done, else services should be stopped"*. The API is what stops it — every
 * tenant-scoped route answers `402 TRIAL_ENDED` — and this is the screen that
 * says so once, rather than eleven panels each reporting their own failure.
 *
 * **It is not an error state and is deliberately not styled as one.** Nothing has
 * gone wrong: a trial ended, which is the thing trials do. The page states what
 * happened, what it means for their data, and the one action that resolves it.
 *
 * **Billing stays reachable, and so does the switcher.** Those are the two ways
 * out — buy a plan, or go and work in another organisation — and they are exactly
 * the paths `UNGATED_PREFIXES` keeps open on the server. A screen that trapped
 * somebody with no working link would be worse than the refusal it explains.
 *
 * Rendered by `RequireTenant`, so it replaces the whole shell rather than
 * appearing inside it: a sidebar full of navigation to screens that all answer
 * 402 is an invitation to eleven dead ends.
 */
export function TrialEnded(): JSX.Element {
  const { tenant } = useActiveTenant()
  const { memberships } = useTenants()
  const location = useLocation()

  // The billing screen is the way out, so a request already heading there is let
  // through rather than intercepted — otherwise pressing *Choose a plan* would
  // land back on this page.
  const elsewhere = memberships.filter((row) => row.tenant.id !== tenant.id).length

  return (
    <div className="app-full-state">
      <section className="ar-card" style={{ maxWidth: '34rem' }}>
        <div className="ar-card__header">
          <h1 className="ar-h2">Your free trial has ended</h1>
        </div>
        <div className="ar-card__body ar-stack">
          <p>
            The seven-day trial for <strong>{tenant.name}</strong> is over
            {tenant.trial_ends_at ? ` — it ended on ${endedOn(tenant.trial_ends_at)}` : ''}. Audits,
            content and outreach are stopped until a plan is chosen.
          </p>
          {/* The reassurance that decides whether somebody comes back: nothing is
              being deleted while they think about it. Said plainly, because the
              alternative is a support ticket asking. */}
          <p className="ar-muted">
            Nothing has been deleted. Your sites, keywords, drafts and leads are exactly as you left
            them, and choosing a plan picks up where the trial stopped.
          </p>
        </div>
        <div className="ar-card__footer">
          <Link
            className="ar-btn ar-btn--primary"
            to="/billing"
            state={{ from: location.pathname }}
          >
            Choose a plan
          </Link>
          {elsewhere > 0 ? (
            <Link className="ar-btn ar-btn--ghost" to="/organisations">
              Switch organisation
            </Link>
          ) : null}
        </div>
      </section>
    </div>
  )
}

/** The end date, in the tenant's zone — the only one this product renders in. */
function endedOn(iso: string): string {
  return new Date(iso).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'Asia/Kolkata',
  })
}
